<template>
  <footer class="bg-background border-t border-border" aria-labelledby="footer-heading" id="company">
    <h2 id="footer-heading" class="sr-only">Footer</h2>

    <div class="px-6 py-16 mx-auto max-w-7xl lg:px-8 lg:py-20">
      <div class="grid grid-cols-1 gap-12 lg:grid-cols-3 lg:gap-16">
        <!-- Left Column: Branding & Newsletter -->
        <div class="lg:col-span-2">
          <!-- Logo & Tagline -->
          <div class="mb-8">
            <VimiLogo class="h-16 w-auto" />
            <p class="mt-4 text-base leading-relaxed text-muted-foreground max-w-md">
              Professional-grade crypto portfolio tracking for serious traders. Track, analyze, and optimize your trading strategy with real-time insights.
            </p>
          </div>

          <!-- Newsletter -->
          <div class="mt-10">
            <h3 class="text-lg font-semibold text-foreground mb-4">
              Subscribe to our newsletter
            </h3>
            <p class="text-sm text-muted-foreground mb-4">
              The latest news, articles, and resources, sent to your inbox regularly.
            </p>
            <form @submit.prevent="handleNewsletterSubmit" class="flex flex-col gap-3 sm:flex-row sm:max-w-md">
              <input
                v-model="newsletterEmail"
                type="email"
                name="email-address"
                id="email-address"
                autocomplete="email"
                required
                class="flex-1 px-4 py-2.5 text-base transition-all duration-200 rounded-lg bg-background text-foreground border border-input focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent placeholder:text-muted-foreground"
                placeholder="Enter your email" />
              <button
                type="submit"
                :disabled="isSubscribing"
                class="px-6 py-2.5 text-base font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap">
                <span v-if="!isSubscribing">Subscribe</span>
                <span v-else class="flex items-center gap-2">
                  <UIcon name="i-heroicons-arrow-path" class="w-4 h-4 animate-spin" />
                  Subscribing...
                </span>
              </button>
            </form>
            <p v-if="subscribeSuccess" class="mt-3 text-sm text-green-600 dark:text-green-400">
              ✓ Thanks for subscribing!
            </p>
          </div>
        </div>

        <!-- Right Column: Links -->
        <div class="grid grid-cols-2 gap-8 lg:gap-12">
          <!-- Support Links -->
          <div>
            <h3 class="text-sm font-semibold text-foreground mb-4">
              Support
            </h3>
            <ul role="list" class="space-y-3">
              <li v-for="item in navigation.support" :key="item.name">
                <a
                  :href="item.href"
                  class="text-sm text-muted-foreground hover:text-secondary transition-colors duration-200">
                  {{ item.name }}
                </a>
              </li>
            </ul>
          </div>

          <!-- Legal Links -->
          <div>
            <h3 class="text-sm font-semibold text-foreground mb-4">
              Legal
            </h3>
            <ul role="list" class="space-y-3">
              <li v-for="item in navigation.legal" :key="item.name">
                <a
                  :href="item.href"
                  class="text-sm text-muted-foreground hover:text-secondary transition-colors duration-200">
                  {{ item.name }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Bottom Section: Copyright & Social -->
      <div class="flex flex-col items-center justify-between gap-6 pt-8 mt-12 border-t border-border md:flex-row">
        <!-- Copyright -->
        <p class="text-sm text-muted-foreground text-center md:text-left">
          &copy; {{ currentYear }} Vimi Pussewala Hewage. Org.nr: 929487125. All rights reserved.
        </p>

        <!-- Social Links -->
        <div class="flex items-center gap-4">
          <a
            v-for="item in navigation.social"
            :key="item.name"
            :href="item.href"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center justify-center w-10 h-10 transition-all duration-200 rounded-lg bg-card border border-border hover:bg-accent hover:border-primary/30 hover:scale-105 group"
            :aria-label="item.name">
            <UIcon
              :name="item.icon"
              class="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors duration-200"
              aria-hidden="true" />
          </a>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
const navigation = {
  support: [
    { name: "Pricing", href: "#pricing" },
    { name: "Documentation", href: "https://docs.vimicrypto.com/" },
    { name: "Contact Us", href: "#support" },
    { name: "FAQ", href: "#faq" },
  ],
  legal: [
    { name: "Terms of Service", href: "/terms" },
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Cookie Policy", href: "/cookies" },
  ],
  social: [
    {
      name: "Twitter",
      href: "https://twitter.com/viminorge",
      icon: "ri:twitter-x-fill",
    },
    {
      name: "GitHub",
      href: "https://github.com/viminorge",
      icon: "ri:github-fill",
    },
    {
      name: "LinkedIn",
      href: "https://linkedin.com/company/viminorge",
      icon: "ri:linkedin-fill",
    },
  ],
};

const newsletterEmail = ref('');
const isSubscribing = ref(false);
const subscribeSuccess = ref(false);

const currentYear = new Date().getFullYear();

const handleNewsletterSubmit = async () => {
  isSubscribing.value = true;

  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 1000));

  // Show success
  subscribeSuccess.value = true;
  isSubscribing.value = false;
  newsletterEmail.value = '';

  // Hide success message after 3 seconds
  setTimeout(() => {
    subscribeSuccess.value = false;
  }, 3000);
};
</script>
